import type { StatusChipTone } from '@/components/ui/status-chip';
import type {
  AnswerType,
  AnswerValue,
  Candidate,
  CoverageChange,
  CreateInterviewBody,
  InterviewPlan,
  InterviewState,
  InterviewStatus,
} from '@/lib/api/discovery-sessions';
import type { ConfirmPlacementBody } from '@/lib/api/discovery-interview-proposals';
import { nameProblem, plural } from '../discovery-logic';

/**
 * Pure helpers behind Discovery -> Sessions (T-0186). They word and arrange
 * what the API computed (states, counts, coverage changes); nothing here
 * counts evidence or sources. Prototype: `sessionsView`, `sessionDetail`,
 * `planBody`, `questBody`, `addInterviewForm`, `roundDetail` and
 * `personDetail` in `docs/discovery/prototype/discovery.js`.
 */

// ---------------------------------------------------------------------------
// Words for states
// ---------------------------------------------------------------------------

/** The list chip of an interview (`sessState` in the prototype). */
export function stateChip(state: InterviewState): { label: string; tone: StatusChipTone } {
  switch (state.kind) {
    case 'cancelled':
      return { label: 'Cancelled', tone: 'frozen' };
    case 'reading':
      return {
        label: state.progress != null ? `Reading ${state.progress}%` : 'Reading',
        tone: 'ai',
      };
    case 'match_speakers':
      return { label: 'Match speakers', tone: 'amber' };
    case 'review':
      return { label: `${state.pending ?? 0} to review`, tone: 'amber' };
    case 'reading_failed':
      return { label: 'Reading failed', tone: 'at-risk' };
    case 'held':
      return { label: 'Held · upload transcript', tone: 'amber' };
    case 'notes_entered':
      return { label: 'Notes entered', tone: 'healthy' };
    case 'reviewed':
      return state.nothing_found
        ? { label: 'Reviewed · nothing found', tone: 'amber' }
        : { label: 'Reviewed', tone: 'healthy' };
    case 'scheduled':
      return { label: 'Scheduled', tone: 'neutral' };
    case 'not_scheduled':
      return { label: 'Not scheduled', tone: 'unassessed' };
    default:
      return { label: 'Optional', tone: 'frozen' };
  }
}

/** An interview's status where only the status is known (a person's interviews). */
export function statusChip(status: string): { label: string; tone: StatusChipTone } {
  switch (status as InterviewStatus) {
    case 'held':
      return { label: 'Held', tone: 'healthy' };
    case 'scheduled':
      return { label: 'Scheduled', tone: 'neutral' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'frozen' };
    default:
      return { label: 'Not scheduled', tone: 'unassessed' };
  }
}

// ---------------------------------------------------------------------------
// The Sessions list by moment (the interview simplification, 23 Oct 2026)
// ---------------------------------------------------------------------------

/**
 * Where an interview is listed: after the session (it needs you: speakers to
 * match, items to review, a transcript to upload or read again, or being
 * read), coming up (scheduled, or someone named with no date yet),
 * reviewed, not planned yet (a plan row with no one named and no date, or
 * optional), or cancelled.
 */
export type Moment = 'after' | 'coming' | 'reviewed' | 'unplanned' | 'cancelled';

export function momentOf(i: { state: InterviewState; people: ReadonlyArray<unknown> }): Moment {
  switch (i.state.kind) {
    case 'cancelled':
      return 'cancelled';
    case 'reading':
    case 'match_speakers':
    case 'review':
    case 'reading_failed':
    case 'held':
      return 'after';
    case 'reviewed':
    case 'notes_entered':
      return 'reviewed';
    case 'scheduled':
      return 'coming';
    default:
      return i.people.length > 0 ? 'coming' : 'unplanned';
  }
}

/** After the session, what blocks first: speakers to match, then review, then the rest, then reading. */
const AFTER_RANK: Partial<Record<InterviewState['kind'], number>> = {
  match_speakers: 0,
  review: 1,
  held: 2,
  reading_failed: 2,
  reading: 3,
};

export function byWhatBlocks(a: { state: InterviewState }, b: { state: InterviewState }): number {
  return (AFTER_RANK[a.state.kind] ?? 9) - (AFTER_RANK[b.state.kind] ?? 9);
}

/**
 * The one next thing, as a chip: "Match speakers", "22 to review", "Reading
 * 64%", "Upload transcript", the date of a scheduled interview, "No date
 * yet", "Not planned", "Reviewed", "Optional", "Cancelled".
 */
export function momentChip(
  state: InterviewState,
  scheduledAt: string | null,
  /** Someone is named: a row with no one is "Not planned", one with no date "No date yet". */
  named = true,
): { label: string; tone: StatusChipTone } {
  switch (state.kind) {
    case 'held':
      return { label: 'Upload transcript', tone: 'amber' };
    case 'scheduled':
      return { label: scheduledAt ? shortDay(scheduledAt) : 'Scheduled', tone: 'neutral' };
    case 'not_scheduled':
      return { label: named ? 'No date yet' : 'Not planned', tone: 'unassessed' };
    case 'notes_entered':
      return { label: 'Reviewed · notes', tone: 'healthy' };
    default:
      return stateChip(state);
  }
}

/** "Payroll manager · S6", "3 people · S16", "S5 · no one named". */
export function listMeta(i: {
  code: string;
  title: string;
  people: ReadonlyArray<{ full_name: string; title?: string | null }>;
}): string {
  if (i.people.length === 0) return `${i.code} · no one named`;
  if (i.people.length > 1) return `${plural(i.people.length, 'person', 'people')} · ${i.code}`;
  return `${i.people[0]!.title || i.title} · ${i.code}`;
}

/** The list's title: the person when there is one, else the interview's own title. */
export const listTitle = (i: { title: string; people: ReadonlyArray<{ full_name: string }> }) =>
  i.people.length === 1 ? i.people[0]!.full_name : i.title;

/** How the queue and the pages name an interview: "Maria Lopez (S6)", or "Department heads round table (S22)". */
export function nameOfInterview(
  items: ReadonlyArray<{
    id: string;
    code: string;
    title: string;
    people: ReadonlyArray<{ full_name: string }>;
  }>,
) {
  return (iv: { id: string; code: string; title: string }) => {
    const found = items.find((x) => x.id === iv.id);
    return `${found ? listTitle(found) : iv.title} (${iv.code})`;
  };
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const asDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The prototype's short months ("24 Sep"): `toLocaleDateString('en-GB')` says "Sept".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');
const dayMonth = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;

/** "24 Sep 2026 · 10:00", in local time */
export function formatWhen(iso: string | null | undefined): string {
  const d = asDate(iso);
  return d ? `${dayMonth(d)} ${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}` : '';
}

/** "24 Sep" */
export function shortDay(iso: string | null | undefined): string {
  const d = asDate(iso);
  return d ? dayMonth(d) : '';
}

/** "24 Sep 2026" */
export function longDay(iso: string | null | undefined): string {
  const d = asDate(iso);
  return d ? `${dayMonth(d)} ${d.getFullYear()}` : '';
}

/** The value of an `<input type="datetime-local">` for an instant, in local time. */
export function localInputValue(iso: string | null | undefined): string {
  const d = asDate(iso);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

// ---------------------------------------------------------------------------
// Adding an interview (rule 6, Decisions 54.13, 54.14)
// ---------------------------------------------------------------------------

export interface AddDraft {
  name: string;
  title: string;
  department: string;
  email: string;
  /** The existing person chosen in answer to "Is this …?". */
  sameAs: Candidate | null;
  /** Candidates answered "No, someone else". */
  different: string[];
  /** A type id, `other`, or undefined to take the suggestion from the title. */
  typeId: string | undefined;
  /** Areas picked by hand; undefined means the type's areas. */
  areaIds: string[] | undefined;
  /** Question ticks changed from their default, by plan key. */
  ticks: Record<string, boolean>;
  channel: 'live' | 'link';
  /** `<input type="datetime-local">` value; empty = not scheduled. */
  when: string;
  /** Minutes; null = the type's length. */
  duration: number | null;
}

export const emptyAddDraft = (): AddDraft => ({
  name: '',
  title: '',
  department: '',
  email: '',
  sameAs: null,
  different: [],
  typeId: undefined,
  areaIds: undefined,
  ticks: {},
  channel: 'live',
  when: '',
  duration: null,
});

/** The candidates still waiting for an answer: the add cannot be saved while any are. */
export function unansweredCandidates(
  draft: AddDraft,
  candidates: ReadonlyArray<Candidate>,
): Candidate[] {
  if (draft.sameAs) return [];
  return candidates.filter((c) => !draft.different.includes(c.person_id));
}

export const isIncluded = (key: string, byDefault: boolean, ticks: Record<string, boolean>) =>
  ticks[key] ?? byDefault;

/** Why the add form cannot be saved yet, or null. In the order the form is read. */
export function addProblem(
  draft: AddDraft,
  plan: InterviewPlan | undefined,
  candidates: ReadonlyArray<Candidate>,
): string | null {
  if (!draft.sameAs) {
    if (!draft.name.trim() || nameProblem(draft.name)) return 'Add a first and last name';
    const open = unansweredCandidates(draft, candidates);
    if (open.length > 0) return `Answer whether this is ${open[0]!.full_name}`;
  }
  if (!plan) return 'Pick an interview type or at least one area';
  if (!plan.type && plan.area_ids.length === 0)
    return 'Pick an interview type or at least one area';
  return null;
}

const toIso = (local: string) => {
  const d = local ? new Date(local) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
};

export function buildCreateBody(draft: AddDraft, plan: InterviewPlan): CreateInterviewBody {
  const person = draft.sameAs
    ? { person_id: draft.sameAs.person_id }
    : {
        full_name: draft.name.trim().replace(/\s+/g, ' '),
        ...(draft.title.trim() ? { title: draft.title.trim() } : {}),
        ...(draft.department.trim() ? { department: draft.department.trim() } : {}),
        ...(draft.email.trim() ? { email: draft.email.trim() } : {}),
        ...(draft.different.length > 0
          ? {
              match_decision: {
                decision: 'different' as const,
                candidate_ids: [...draft.different],
              },
            }
          : {}),
      };
  const scheduledAt = toIso(draft.when);
  return {
    people: [person],
    interview_type_id: plan.type?.id ?? null,
    area_ids: [...plan.area_ids],
    questions: plan.questions.map((q) => ({
      kind: q.kind,
      ...(q.kind === 'ai'
        ? { ai_proposal_id: q.ai_proposal_id ?? undefined }
        : { question_id: q.question_id ?? undefined }),
      included: isIncluded(q.key, q.default_included, draft.ticks),
    })),
    demos: plan.demos.map((d) => ({
      question_id: d.question_id,
      included: isIncluded(d.key, d.default_included, draft.ticks),
    })),
    ...(plan.questionnaire ? { questionnaire_channel: draft.channel } : {}),
    ...(scheduledAt ? { scheduled_at: scheduledAt, send_invite: true } : {}),
    duration_minutes: draft.duration ?? plan.duration_minutes,
  };
}

export const toIsoInstant = toIso;

/**
 * The confirm body of a transcript placement from the add form's body
 * (T-0261): who, the type, the areas and the length. The interview is held
 * by the transcript, so no date, no invite, no questionnaire channel; the
 * questions are the plan's.
 */
export function toConfirmBody(body: CreateInterviewBody): ConfirmPlacementBody {
  return {
    people: body.people,
    interview_type_id: body.interview_type_id,
    area_ids: body.area_ids,
    duration_minutes: body.duration_minutes,
  };
}

// ---------------------------------------------------------------------------
// Questionnaire answers (E12-F08 §3)
// ---------------------------------------------------------------------------

/** What is being typed or picked for one question, before it is saved. */
export type AnswerDraft = string | number | string[];

export function answerValue(type: AnswerType, draft: AnswerDraft | undefined): AnswerValue | null {
  if (draft === undefined) return null;
  switch (type) {
    case 'rating':
      return typeof draft === 'number' ? { rating: draft } : null;
    case 'single':
      return typeof draft === 'string' && draft ? { option: draft } : null;
    case 'multiple':
      return Array.isArray(draft) && draft.length > 0 ? { options: draft } : null;
    default: {
      const t = typeof draft === 'string' ? draft.trim() : '';
      return t ? { text: t } : null;
    }
  }
}

// ---------------------------------------------------------------------------
// Coverage changes in words (Decision 53.6)
// ---------------------------------------------------------------------------

const STATUS_WORD = {
  corroborated: 'covered',
  partial: 'still short of sources',
  none: 'without evidence',
};

/** After a write: "Bank reconciliation is now covered." or "Coverage unchanged." */
export function coverageMoved(
  changes: ReadonlyArray<CoverageChange>,
  labelOf: (areaId: string) => string,
): string {
  const moved = changes.filter((c) => c.status_after && c.status_before !== c.status_after);
  if (moved.length === 0) return '';
  return moved
    .map((c) => `${c.label ?? labelOf(c.area_id)} is now ${STATUS_WORD[c.status_after!]}`)
    .join('. ')
    .concat('.');
}

/** Before a confirm: "Bank reconciliation: 1 → 2 sources (covered)" per area (`previewEffect`). */
export function coverageEffect(
  changes: ReadonlyArray<CoverageChange>,
  labelOf: (areaId: string) => string,
): string[] {
  return changes
    .filter((c) => c.sources_after !== c.sources_before || c.same_person_only)
    .map((c) => {
      const label = c.label ?? labelOf(c.area_id);
      if (c.sources_after > c.sources_before) {
        const covered =
          c.status_after === 'corroborated' && c.status_before !== 'corroborated'
            ? ' (covered)'
            : '';
        return `${label}: ${c.sources_before} → ${plural(c.sources_after, 'source')}${covered}`;
      }
      return `${label}: more evidence, still ${plural(c.sources_after, 'source')} (same person)`;
    });
}

/** "S12 cancelled." style toasts name the interview by its code and title. */
export const interviewName = (d: { code: string; title: string }) => `${d.code} ${d.title}`;
